import React, { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import cx from 'classnames';
import { Tracking } from 'web-core/src/tracking';
import { HighlightHeaderType } from 'client/components/highlightHeader';
import { ContentfulGalleryType, ContentfulImageSetType } from 'types/globals/contentful';
import StrideHeaderWrapper from 'client/components/strideHeaderWrapper';
import useScrollY from 'client/hooks/useScrollY';
import parseHighlightText from 'client/utils/parseHighlight';

import 'client/styles/common.scss';

import { WEB_VIEW_PARTNER_LANDING_PAGE } from '../constants/tracking';
import Testimonials, { TestimonialsType } from './landing-page/testimonialsSection';
import HealthCoverage, { HealthCoverageCta } from './landing-page/healthCoverageSection';
import SaveOnExpensesSection, { expensesSectionCta } from './landing-page/saveOnExpensesSection';
import SaveOnTaxesSection, { taxesSectionCta } from './landing-page/saveOnTaxesSection';
import HeroSection, { HeroExtraConfig } from './landing-page/heroSection';

import '../styles/landingPage.scss';

type ContentfulJoinLandingPage = {
  hero: ContentfulImageSetType;
  testimonials: TestimonialsType;
  testimonialHeader: HighlightHeaderType;
  benefits: ContentfulGalleryType;
  benefitsCta: HealthCoverageCta;
  taxesGallery: ContentfulGalleryType;
  expensesGallery: ContentfulGalleryType;
};

const mapStateToProps = ({
  contentfulData: { joinLandingPage },
}: {
  contentfulData: { joinLandingPage: ContentfulJoinLandingPage };
}) => ({
  joinLandingPage,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & {
  brand: string;
};

const LandingPage = ({ brand, joinLandingPage }: Props) => {
  const {
    hero,
    testimonialHeader,
    testimonials,
    benefits,
    benefitsCta,
    taxesGallery,
    expensesGallery,
  } = joinLandingPage;

  useEffect(() => {
    Tracking.track(WEB_VIEW_PARTNER_LANDING_PAGE, { client: brand });
  }, [brand]);

  return (
    <StrideHeaderWrapper showPhone>
      <div className="enterprise-landing-page medium-pad-top">
        <HeroSection
          title={hero.name}
          description={hero.caption}
          image={hero.main}
          extraConfig={hero.extraConfig as HeroExtraConfig} />

        <HealthCoverage
          header={parseHighlightText(benefits.name)}
          items={benefits.images}
          subHeader={benefits.description}
          cta={benefitsCta} />

        <Testimonials testimonialHeader={testimonialHeader} testimonials={testimonials} />

        <SaveOnTaxesSection
          header={parseHighlightText(taxesGallery.name)}
          items={taxesGallery.images}
          subHeader={taxesGallery.description}
          cta={taxesGallery.extraConfig?.cta as taxesSectionCta} />

        <SaveOnExpensesSection
          header={parseHighlightText(expensesGallery.name)}
          items={expensesGallery.images}
          subHeader={expensesGallery.description}
          cta={expensesGallery.extraConfig?.cta as expensesSectionCta} />
      </div>
    </StrideHeaderWrapper>
  );
};

export default connector(LandingPage);
